import { Injectable } from '@angular/core';
import Dexie, { Table } from 'dexie';

@Injectable({
  providedIn: 'root',
})
export class SourceCoreDexieService extends Dexie {
  private tableRegistry: { [tableName: string]: Table<any, string> } = {};
  private migrationPromise: Promise<void> = Promise.resolve();

  constructor() {
    super('SourceCore');
    this.version(1).stores({});
  }

  public ensureTable(tableName: string): Promise<void> {
    this.migrationPromise = this.migrationPromise.then(() => this.migrate(tableName));
    return this.migrationPromise;
  }

  private async migrate(tableName: string): Promise<void> {
    if (this.tableRegistry[tableName]) {
      return;
    }

    // All tables will have a primary key 'id'
    const schema = { ...this.tables.reduce((acc, table) => ({ ...acc, [table.name]: table.schema.primKey.src }), {}), [tableName]: 'id' };

    this.close();
    this.version(this.verno + 1).stores(schema);
    await this.open();

    this.tableRegistry[tableName] = this.table(tableName);
  }

  private getTable<T>(tableName: string): Table<T, string> {
    const table = this.tableRegistry[tableName];
    if (!table) {
      throw new Error(`Table "${tableName}" not found. Please call ensureTable first.`);
    }
    return table;
  }

  async getLearningSourceDbData<T>(tableName: string): Promise<Record<string, T> | undefined> {
    const table = this.getTable<T>(tableName);
    return table.toArray().then((data) => {
      const dict: Record<string, T> = {};
      data.forEach((item: any) => {
        dict[item.id] = item;
      });
      return dict;
    });
  }

  async bulkUpsertKnowledgeCoreData<T extends { id?: string }>(tableName: string, rows: T[]): Promise<string[]> {
    const table = this.getTable<T>(tableName);
    await table.bulkPut(rows);
    return rows.map((s) => s.id).filter((id) => id !== undefined) as string[];
  }

  async clearAllData(tableName: string): Promise<void> {
    const table = this.getTable(tableName);
    await table.clear();
  }

  async deleteKnowledgeSource(tableName: string, id: string): Promise<void> {
    const table = this.getTable(tableName);
    return table.delete(id);
  }

  async deleteDatabase(): Promise<void> {
    return this.delete();
  }
}
